import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, ShoppingBag, Video, ArrowLeft, Music2, Share2, Heart, BookOpen } from "lucide-react";
import { toast } from "sonner";
import BuyDialog from "@/components/BuyDialog";
import { isSaved, toggleSaved } from "@/lib/saved";

export default function SongDetail() {
  const { id } = useParams();
  const [song, setSong] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyFormat, setBuyFormat] = useState("mp3");
  const [saved, setSaved] = useState(false);
  const videoRef = useRef(null);
  const { current, playing, play, toggle } = usePlayer();
  const isCurrent = current && song && current.id === song.id;

  useEffect(() => {
    if (!song) return;
    setSaved(isSaved(song.id));
    const h = () => setSaved(isSaved(song.id));
    window.addEventListener("sanctum-saved-change", h);
    return () => window.removeEventListener("sanctum-saved-change", h);
  }, [song]);

  const openBuy = (fmt) => { setBuyFormat(fmt); setBuyOpen(true); };

  const share = async () => {
    if (!song) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${backend}/api/share/${song.id}`;
    const shareData = {
      title: `${song.title} — ${song.artist}`,
      text: song.description || "Listen on Sanctum",
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch (_) {}
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard");
    } catch { toast.error("Could not copy link"); }
  };

  useEffect(() => {
    api.get(`/songs/${id}`).then((r) => setSong(r.data)).catch(() => setSong(null));
  }, [id]);

  // 30s cap on video preview
  useEffect(() => {
    if (!showVideo || !videoRef.current) return;
    const v = videoRef.current;
    const handler = () => { if (v.currentTime >= 30) v.pause(); };
    v.addEventListener("timeupdate", handler);
    return () => v.removeEventListener("timeupdate", handler);
  }, [showVideo]);

  if (!song) return <div className="max-w-4xl mx-auto px-6 py-24 text-neutral-500 text-sm">Loading song…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/songs" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-neutral-900 mb-8 tracking-widest uppercase">
        <ArrowLeft size={14} strokeWidth={1.5} /> All songs
      </Link>

      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <div className="art-frame rounded-3xl overflow-hidden bg-neutral-100 aspect-square shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
            {showVideo && song.video_url ? (
              <video
                ref={videoRef}
                data-testid="video-preview"
                src={song.video_url}
                poster={song.cover_url}
                controls
                autoPlay
                className="w-full h-full object-cover"
              />
            ) : (
              <img src={song.cover_url} alt={song.title} className="w-full h-full object-cover" />
            )}
          </div>
        </div>

        <div className="flex flex-col justify-center">
          <p className="eyebrow mb-3">{song.album || "Single"}</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight font-light leading-none">
            {song.title}
          </h1>
          <p className="mt-3 text-neutral-500 text-sm tracking-wide flex items-center gap-1.5">
            <Music2 size={13} strokeWidth={1.5} /> {song.artist} · {song.genre}
          </p>
          {song.description && (
            <p className="mt-6 text-neutral-600 leading-relaxed max-w-md">{song.description}</p>
          )}

          {song.verse && (
            <blockquote
              data-testid="song-verse"
              className="mt-8 max-w-md border-l-2 border-[--sanctum-accent] pl-5 py-1"
            >
              <p className="font-display text-xl italic text-neutral-700 leading-relaxed">"{song.verse}"</p>
              {song.verse_ref && (
                <cite className="not-italic text-xs tracking-widest uppercase text-neutral-500 mt-2 inline-flex items-center gap-1.5">
                  <BookOpen size={11} strokeWidth={1.5} /> {song.verse_ref}
                </cite>
              )}
            </blockquote>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              data-testid="detail-listen-btn"
              onClick={() => (isCurrent ? toggle() : play(song, "preview"))}
              className="btn-pill btn-primary"
            >
              {isCurrent && playing ? <Pause size={14} strokeWidth={1.5} /> : <Play size={14} strokeWidth={1.5} />}
              {isCurrent && playing ? "Pause" : "Listen · 30s preview"}
            </button>
            <button
              data-testid="detail-buy-mp3"
              onClick={() => openBuy("mp3")}
              className="btn-pill btn-outline"
            >
              <ShoppingBag size={14} strokeWidth={1.5} /> Buy MP3 · $1.99
            </button>
            <button
              data-testid="detail-share-btn"
              onClick={share}
              className="btn-pill btn-outline"
            >
              <Share2 size={14} strokeWidth={1.5} /> Share
            </button>
            <button
              data-testid="detail-save-btn"
              onClick={() => toggleSaved(song.id)}
              className={`btn-pill ${saved ? "bg-[--sanctum-accent] text-neutral-900" : "btn-outline"}`}
              aria-label={saved ? "Unsave" : "Save for later"}
            >
              <Heart size={14} strokeWidth={1.5} fill={saved ? "currentColor" : "none"} />
              {saved ? "Saved" : "Save"}
            </button>
            {song.video_url && (
              <>
                <button
                  data-testid="detail-watch-btn"
                  onClick={() => setShowVideo((s) => !s)}
                  className="btn-pill btn-outline"
                >
                  <Video size={14} strokeWidth={1.5} /> {showVideo ? "Show cover" : "Watch video"}
                </button>
                <button
                  data-testid="detail-buy-mp4"
                  onClick={() => openBuy("mp4")}
                  className="btn-pill btn-primary"
                >
                  <Video size={14} strokeWidth={1.5} /> Buy MP4 · $2.99
                </button>
              </>
            )}
          </div>

          <div className="mt-10 pt-8 border-t border-[--sanctum-border] grid grid-cols-2 gap-6 text-sm">
            <div>
              <p className="eyebrow mb-1">Format</p>
              <p>MP3 audio {song.video_url && "· MP4 video"}</p>
            </div>
            <div>
              <p className="eyebrow mb-1">Delivery</p>
              <p>Instant email receipt with download link</p>
            </div>
          </div>
        </div>
      </div>
      <BuyDialog open={buyOpen} onOpenChange={setBuyOpen} song={song} format={buyFormat} />
    </div>
  );
}
